import type { ReactNode } from 'react';
import { TicketStatus, TicketPriority, TicketSource, UserRole } from './api';

// Date range presets
export const DATE_RANGE_PRESETS = {
  TODAY: 'today',
  YESTERDAY: 'yesterday',
  THIS_WEEK: 'thisWeek',
  LAST_WEEK: 'lastWeek',
  THIS_MONTH: 'thisMonth',
  LAST_MONTH: 'lastMonth',
  LAST_7_DAYS: 'last7Days',
  LAST_30_DAYS: 'last30Days',
  LAST_90_DAYS: 'last90Days',
  CUSTOM: 'custom'
} as const;

export type DateRangePreset = typeof DATE_RANGE_PRESETS[keyof typeof DATE_RANGE_PRESETS];

// Date range configuration
export interface DateRange {
  from?: Date | string;
  to?: Date | string;
  preset?: DateRangePreset;
}

// Custom field filter types
export interface CustomFieldFilter {
  fieldId: string;
  fieldName: string;
  fieldType: 'text' | 'number' | 'dropdown' | 'checkbox' | 'date';
  operator: 'equals' | 'contains' | 'startsWith' | 'endsWith' | 'greaterThan' | 'lessThan' | 'between' | 'in' | 'notIn';
  value: any;
  values?: any[]; // For multiple selection
}

// User/Agent filter configuration
export interface UserFilter {
  userId: string;
  userName: string;
  userEmail: string;
  userRole: UserRole;
  avatar?: string;
  groupId?: string;
  groupName?: string;
}

// Company/Customer filter configuration  
export interface CompanyFilter {
  companyId: string;
  companyName: string;
  domain?: string;
  isVip?: boolean;
  tier?: 'free' | 'basic' | 'pro' | 'enterprise';
}

// Advanced filter state
export interface AdvancedFilters {
  // Search and basic filters
  search: string;
  
  // Agent & Assignment
  assignedAgents: string[];
  assignedGroups: string[];
  unassignedOnly: boolean;
  myTicketsOnly: boolean;
  
  // Ticket Properties
  statuses: TicketStatus[];
  priorities: TicketPriority[];
  sources: TicketSource[];
  types: string[];
  
  // Customer Information
  customers: string[];
  companies: string[];
  vipCustomersOnly: boolean;
  customerSatisfactionRange: [number, number] | null;
  
  // Date Ranges
  createdDateRange: DateRange | null;
  updatedDateRange: DateRange | null;
  dueDateRange: DateRange | null;
  closedDateRange: DateRange | null;
  
  // Custom Fields
  customFields: CustomFieldFilter[];
  
  // Tags and Labels
  tags: string[];
  excludeTags: string[];
  
  // Advanced options
  hasAttachments: boolean | null;
  hasConversations: boolean | null;
  isOverdue: boolean | null;
  escalated: boolean | null;
}

// Filter group configuration
export interface FilterGroupConfig {
  id: string;
  title: string;
  icon?: ReactNode;
  defaultExpanded: boolean;
  collapsible: boolean;
  filters: FilterConfig[];
  visible?: boolean;
  order: number;
}

// Individual filter configuration
export interface FilterConfig {
  id: string;
  type: FilterType;
  title: string;
  description?: string;
  placeholder?: string;
  options?: FilterOption[];
  multiple?: boolean;
  searchable?: boolean;
  clearable?: boolean;
  required?: boolean;
  defaultValue?: any;
  visible?: boolean;
  order: number;
  validation?: (value: any) => string | null;
  dependency?: {
    filterId: string;
    values: any[];
  };
}

export type FilterType = 
  | 'search'
  | 'select' 
  | 'multiselect'
  | 'toggle'
  | 'dateRange'
  | 'userSelect'
  | 'companySelect'
  | 'tagSelect'
  | 'customField'
  | 'slider'
  | 'checkbox'
  | 'radio';

export interface FilterOption {
  value: any;
  label: string;
  description?: string;
  icon?: ReactNode;
  color?: string;
  count?: number;
  disabled?: boolean;
  group?: string;
}

// Saved views system
export interface SavedView {
  id: string;
  name: string;
  description?: string;
  filters: AdvancedFilters;
  isPublic: boolean;
  isDefault: boolean;
  createdBy: string;
  createdByName: string;
  createdAt: string;
  updatedAt: string;
  usageCount?: number;
  tags?: string[];
}

export interface SavedViewsState {
  views: SavedView[];
  currentViewId: string | null;
  loading: boolean;
  error: string | null;
}

// Filter state management
export interface FilterState {
  filters: AdvancedFilters;
  sidebarExpanded: boolean;
  groupExpanded: Record<string, boolean>;
  activeFilters: ActiveFilter[];
  savedViews: SavedViewsState;
  filterOptions: FilterOptionsState;
  loading: boolean;
  error: string | null;
}

// Active filter representation for chips
export interface ActiveFilter {
  id: string;
  groupId: string;
  filterId: string;
  label: string;
  value: any;
  displayValue: string;
  removable: boolean;
  color?: string;
}

// Filter options state (for dropdowns, etc.)
export interface FilterOptionsState {
  agents: UserFilter[];
  groups: Array<{ id: string; name: string; agentCount: number }>;
  companies: CompanyFilter[];
  customers: UserFilter[];
  tags: Array<{ tag: string; count: number }>;
  customFields: Array<{
    id: string;
    name: string;
    type: string;
    options?: any[];
  }>;
  loading: {
    agents: boolean;
    groups: boolean;
    companies: boolean;
    customers: boolean;
    tags: boolean;
    customFields: boolean;
  };
}

// Filter sidebar props
export interface FilterSidebarProps {
  filters: AdvancedFilters;
  onFiltersChange: (filters: AdvancedFilters) => void;
  
  expanded: boolean;
  onToggle: () => void;
  
  savedViews: SavedView[];
  currentViewId: string | null;
  onSaveView: (name: string, description?: string) => void;
  onLoadView: (viewId: string) => void;
  onDeleteView: (viewId: string) => void;
  onSetDefaultView: (viewId: string) => void;
  
  filterOptions: FilterOptionsState;
  onLoadFilterOptions: () => void;
  
  className?: string;
  width?: number;
  position?: 'left' | 'right';
  overlay?: boolean;
  
  // Customization
  enabledGroups?: string[];
  groupConfig?: Partial<Record<string, FilterGroupConfig>>;
  
  // User context
  userRole: UserRole;
  userId: string;
  
  // Callbacks
  onSearchSuggestions?: (query: string) => Promise<string[]>;
  onValidateFilters?: (filters: AdvancedFilters) => Promise<string[]>;
  
  // Analytics
  onFilterAnalytics?: (event: string, data: any) => void;
}

// Filter group component props
export interface FilterGroupProps {
  config: FilterGroupConfig;
  filters: AdvancedFilters;
  onFiltersChange: (filters: AdvancedFilters) => void;
  
  expanded: boolean;
  onToggle: () => void;
  
  filterOptions: FilterOptionsState;
  
  className?: string;
  userRole: UserRole;
  
  // Callbacks
  onSearchSuggestions?: (filterId: string, query: string) => Promise<any[]>;
}

// Individual filter component props
export interface FilterControlProps {
  config: FilterConfig;
  value: any;
  onChange: (value: any) => void;
  
  options?: FilterOption[];
  loading?: boolean;
  error?: string;
  
  className?: string;
  disabled?: boolean;
  
  // Callbacks
  onSearch?: (query: string) => Promise<any[]>;
  onLoadOptions?: () => Promise<FilterOption[]>;
}

// Saved views management props
export interface SavedViewsProps {
  views: SavedView[];
  currentViewId: string | null;
  currentFilters: AdvancedFilters;
  
  onSave: (name: string, description?: string, isPublic?: boolean) => void;
  onLoad: (viewId: string) => void;
  onDelete: (viewId: string) => void;
  onUpdate?: (viewId: string, updates: Partial<SavedView>) => void;
  onSetDefault: (viewId: string) => void;
  onDuplicate?: (viewId: string, newName: string) => void;
  
  loading: boolean;
  error?: string;
  
  className?: string;
  userRole: UserRole;
  
  // UI options
  showCreateButton?: boolean;
  showPublicViews?: boolean;
  maxViews?: number;
}

// Filter chips props
export interface FilterChipsProps {
  activeFilters: ActiveFilter[];
  onRemoveFilter: (filterId: string) => void;
  onClearAll: () => void;
  
  className?: string;
  maxVisible?: number;
  showClearAll?: boolean;
  
  // Customization
  renderChip?: (filter: ActiveFilter) => ReactNode;
}

// Filter search props
export interface FilterSearchProps {
  value: string;
  onChange: (value: string) => void;
  onClear: () => void;
  
  placeholder?: string;
  suggestions?: string[];
  onSuggestionSelect?: (suggestion: string) => void;
  
  loading?: boolean;
  className?: string;
}

// Utility types for filter operations
export interface FilterOperation {
  type: 'add' | 'remove' | 'update' | 'clear';
  filterId: string;
  value?: any;
  previousValue?: any;
}

export interface FilterValidationResult {
  valid: boolean;
  errors: Record<string, string>;
  warnings: Record<string, string>;
}

// URL synchronization
export interface FilterUrlConfig {
  enabled: boolean;
  paramPrefix: string;
  encodeValues: boolean;
  updateBrowserHistory: boolean;
}

// Filter preset configurations
export interface FilterPreset {
  id: string;
  name: string;
  description?: string;
  icon?: ReactNode;
  filters: Partial<AdvancedFilters>;
  userRoles: UserRole[];
  category: 'common' | 'priority' | 'assignment' | 'date' | 'custom';
  order: number;
}

// Export all default presets
export const DEFAULT_FILTER_PRESETS: FilterPreset[] = [
  {
    id: 'open-urgent',
    name: 'Open Urgent Tickets',
    description: 'All open tickets with urgent priority',
    filters: {
      statuses: [TicketStatus.Open],
      priorities: [TicketPriority.Urgent]
    },
    userRoles: [UserRole.Admin, UserRole.Agent],
    category: 'priority',
    order: 1
  },
  {
    id: 'unassigned',
    name: 'Unassigned Tickets',
    description: 'All tickets that need assignment',
    filters: {
      unassignedOnly: true,
      statuses: [TicketStatus.New, TicketStatus.Open, TicketStatus.Pending]
    },
    userRoles: [UserRole.Admin, UserRole.Agent],
    category: 'assignment',
    order: 2
  },
  {
    id: 'my-active',
    name: 'My Active Tickets',
    description: 'Tickets assigned to me that are not closed',
    filters: {
      myTicketsOnly: true,
      statuses: [TicketStatus.Open, TicketStatus.Pending, TicketStatus.OnHold]
    },
    userRoles: [UserRole.Agent],
    category: 'assignment',
    order: 3
  }
];
